import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import { chain, get, isEmpty, size, split } from 'lodash';
import { AUTH_SESSIONS_STATUS } from 'src/constants/authorization';
import { NOTE_AUDITOR_SUBSCRIPTION_ENFORCED, SUBSCRIPTION_ADD_ONS } from 'src/constants/subscriptions';
import { userRoles } from 'src/utils/AppUtils';
import { PRIVILEGES_BY_ROLE, PRIVILEGES_LOCKED_BY_ROLE } from 'src/constants/privileges';

export default {

  userIsAdmin: (state) => get(state.userDetails, 'admin', false), // userIsAdmin()

  userIsSysAdmin: (state) => (get(state.userDetails, 'admin', false)
    && get(state.userDetails, 'sysAdmin', false)), // userIsSysAdmin()

  isSubscriptionPastDue: (state) => (
    (['past_due'].includes(get(state.billing, 'status', ''))
      && (!(state.billing.snoozePastDueUntil instanceof Timestamp)
        || state.billing.snoozePastDueUntil.toMillis() < Date.now())
    )
  ), // isSubscriptionPastDue()

  isSubscriptionUnpaid: (state) => (
    (['unpaid'].includes(get(state.billing, 'status', ''))
      && (!(state.billing.snoozePastDueUntil instanceof Timestamp)
        || state.billing.snoozePastDueUntil.toMillis() < Date.now())
    )
  ), // isSubscriptionUnpaid()

  isSubscriptionInactive: (state) => (
    (['incomplete', 'incomplete_expired', 'unpaid', 'canceled'].includes(get(state.billing, 'status', '')))
    && ((get(state.billing, 'billingType') !== 'free'))
    && (!(state.billing.snoozePastDueUntil instanceof Timestamp)
      || state.billing.snoozePastDueUntil.toMillis() < Date.now())
  ), // isSubscriptionInactive()

  isSubscriptionIncomplete: (state) => (
    (['incomplete'].includes(get(state.billing, 'status', '')))
    && ((get(state.billing, 'billingType') !== 'free'))
    && (!(state.billing.snoozePastDueUntil instanceof Timestamp)
      || state.billing.snoozePastDueUntil.toMillis() < Date.now())
  ), // isSubscriptionIncomplete()

  isSubscriptionUnavailable(state) {
    return this.isSubscriptionIncomplete
      || this.isSubscriptionInactive
      || state.billing?.status === 'created';
  },

  getAvailableLicenses: (state) => (category) => {
    const { quantity = 0, currentUsage = 0 } = state.billing?.subscriptionLicenses?.[category] ?? {};
    return quantity - currentUsage;
  },

  isSubscriptionPremium: (state) => (
    ['premium', 'enterprise'].includes(get(state.billing, 'tier', '')) || get(state.userDetails, 'admin', false)
  ),

  hasPremiumScheduling: (state) => (
    get(
      state.billing,
      ['subscriptionLicenses', 'client', 'addOns', SUBSCRIPTION_ADD_ONS.SCHEDULING, 'price'],
      null,
    ) !== null
  ),

  isSchedulingAvailable(state) {
    return this.hasPremiumScheduling
      && (!state.connectedClinic?.integration || state.integrationSettings.allowScheduling);
  },

  // Billing add-on entitlement. Internal key is `revenueCycle` (customer-facing label is "Billing")
  // to avoid colliding with the `billing` subscription document itself. This is the permanent gate
  // for billing product bets — the pilot group is granted the add-on in Stripe, and every
  // billing-related feature gates on `isRevenueCycleAvailable`.
  hasRevenueCycleAddOn: (state) => (
    get(
      state.billing,
      ['subscriptionLicenses', 'client', 'addOns', SUBSCRIPTION_ADD_ONS.REVENUE_CYCLE, 'price'],
      null,
    ) !== null
  ),

  // Single gate point for billing product bets, mirroring `isSchedulingAvailable`. Currently a
  // straight pass-through of `hasRevenueCycleAddOn` — kept as a distinct getter so future
  // availability rules (integration checks, feature flags) can be added in one place without
  // touching every caller.
  isRevenueCycleAvailable(state) {
    return this.hasRevenueCycleAddOn;
  },

  // Places of service, treatment services and funding sources are BILLING configuration that the
  // scheduling add-on merely owned first — the billing projector reads all three, and so does the
  // client's diagnosis list. Neither add-on alone is the gate: a clinic that schedules needs them
  // for appointments, and a clinic on the billing add-on needs them with no scheduling at all.
  isBillingConfigAvailable(state) {
    return this.isSchedulingAvailable || this.isRevenueCycleAvailable;
  },

  // Generic lookup for a `subscriptionLicenses.client.addOns` entitlement, so the storage path is
  // written once. The router reads it directly for `meta.requiresAddOn`; feature code should use
  // the named getter for its own add-on instead.
  hasClientAddOn: (state) => (addOn) => (
    get(state.billing, ['subscriptionLicenses', 'client', 'addOns', addOn, 'price'], null) !== null
  ),

  hasNoteAuditorAddOn(state) {
    return this.hasClientAddOn(SUBSCRIPTION_ADD_ONS.AI_NOTE_AUDITOR);
  },

  // Organization entitlement for the AI Note Auditor, layered above the `sessions/aiAuditAgent`
  // user privilege — the privilege says who in the clinic may use it, this says whether the clinic
  // got it at all. Deliberately without the Hi Rasmus staff bypass: `resolveAuditorAccess` composes
  // that in where operating the auditor is the question, and keeping it out here lets the domain
  // tell an organization that bought it from one a staff member is demoing inside.
  isNoteAuditorAvailable(state) {
    return !NOTE_AUDITOR_SUBSCRIPTION_ENFORCED || this.hasNoteAuditorAddOn;
  },

  hasInvalidPaymentMethod: (state) => (
    (['incomplete', 'incomplete_expired', 'past_due', 'unpaid'].includes(
      get(state.billing, 'status', ''),
    )
    ) || (
      (!['bankTransfer', 'check'].includes(get(state.billing, 'stripePaymentMethodType')))
      && (!get(state.billing, 'stripePaymentMethodId')))
  ), // hasInvalidPaymentMethod()

  currentClientFirstName: (state) => (
    chain(state.currentClient)
      .get('clientName', '')
      .split(' ')
      .head()
      .value()
  ), // clientFirstName()

  hasTrainerCredentials: (state) => {
    return ([
      userRoles.TRAINER,
      userRoles.SENIOR_THERAPIST,
      userRoles.SUPERVISOR,
      userRoles.ADMINISTRATOR,
      userRoles.MANAGER,
    ].includes(state.userDetails.role));
  }, // hasTrainerCredentials()

  hasTherapistCredentials: (state) => {
    return ([
      userRoles.SENIOR_THERAPIST,
      userRoles.SUPERVISOR,
      userRoles.ADMINISTRATOR,
      userRoles.MANAGER,
    ].includes(state.userDetails.role));
  }, // hasTherapistCredentials()

  hasSupervisorCredentials: (state) => {
    return ([userRoles.SUPERVISOR, userRoles.ADMINISTRATOR, userRoles.MANAGER].includes(state.userDetails.role));
  }, // hasSupervisorCredentials()

  isManager: (state) => state.userDetails.role === userRoles.MANAGER, // isManager()

  isAdministrator: (state) => state.userDetails.role === userRoles.ADMINISTRATOR, // isAdministrator()

  isManagerOrAdministrator() {
    return this.isManager || this.isAdministrator;
  }, // isManagerOrAdministrator()

  isSupervisor: (state) => state.userDetails.role === userRoles.SUPERVISOR, // isSupervisor()

  isSupervisorOrSeniorTherapist() {
    return this.isSupervisor || this.isSeniorTherapist;
  }, // isSupervisorOrSeniorTherapist()

  isSeniorTherapist: (state) => state.userDetails.role === userRoles.SENIOR_THERAPIST, // isSeniorTherapist()

  isTrainer: (state) => state.userDetails.role === userRoles.TRAINER, // isTrainer()

  isParent: (state) => state.userDetails.role === userRoles.PARENT, // isParent()

  isShowLoader: (state) => state.showLoader, // isShowLoader()

  isUserRequiredLoginMethod: (state) => state.userDetails.requireLoginMethod,

  requiredLoginMethod: (state) => (state.userDetails.requireLoginMethod && state.userDetails.loginMethod) || null,

  hasOfflineModeClients({ userSettings: { offlineClients = null } }) {
    return offlineClients && offlineClients.length > 0;
  },

  hasMoreUserAppointments: (state) => {
    return size(state.userAppointments) > state.userAppointmentsLimit;
  },

  hasBusinessHoursWeekdays: (state) => {
    return state.businessHoursSlots.some((slot) => slot.isWeekday);
  },

  userRoles: () => ({
    [userRoles.MANAGER]: {
      label: i18n.t('common.userRoles.manager'),
      forceVisibility: true, // Always show sessions for managers
      level: 100,
    },
    [userRoles.ADMINISTRATOR]: {
      label: i18n.t('common.userRoles.administrator'),
      forceVisibility: true, // Always show sessions for administrators
      level: 200,
    },
    [userRoles.SUPERVISOR]: {
      label: i18n.t('common.userRoles.supervisor'),
      forceVisibility: true, // Always show sessions for supervisors
      level: 400,
    },
    [userRoles.SENIOR_THERAPIST]: {
      label: i18n.t('common.userRoles.senior_therapist'),
      forceVisibility: false,
      level: 500,
    },
    [userRoles.TRAINER]: {
      label: i18n.t('common.userRoles.trainer'),
      forceVisibility: false,
      level: 600,
    },
    [userRoles.PARENT]: {
      label: i18n.t('common.userRoles.parent'),
      forceVisibility: false,
      level: 700,
    },
  }),

  userPrivilegesList(state) {
    const userRoleLabels = i18n.tm('common.userRoles');
    const getPrivilegeItem = (privilegeLabelId) => {
      const [role, privilegeId] = split(privilegeLabelId, '\\');
      return {
        role,
        value: privilegeId || role,
        privilegeLabelId,
        label: get(state.userPrivilegeLabels, privilegeLabelId, get(userRoleLabels, role, '?')),
        roleLabel: get(userRoleLabels, role, '?'),
        level: get(this.userRoles, [role, 'level'], 1000),
        forceVisibility: get(this.userRoles, [role, 'forceVisibility'], false),
        isCustomRole: !!privilegeId,
      };
    };

    return chain(state.userPrivilegeLabels)
      .mapValues((userPrivilegeLabel, privilegeLabelId) => getPrivilegeItem(privilegeLabelId))
      .defaults(
        chain(this.userRoles).mapValues((userRole, role) => getPrivilegeItem(role)).value(),
      )
      .values()
      .orderBy(
        ['level', (privilege) => chain(privilege).get('label').toLower().value()],
        ['asc', 'asc'],
      )
      .value();
  },

  visibilityOptions(state) {
    const userPrivilege = state.userDetails.userPrivilege || state.userDetails.role;
    return this.userPrivilegesList
      .filter(({ forceVisibility }) => !forceVisibility)
      .map((privilege) => ({
        value: privilege.value,
        label: privilege.label,
        hint: privilege.hint,
        disable: (privilege.value === userPrivilege),
      }));
  }, // visibilityOptions()

  hasSessionNotesLayout: (state) => {
    return !isEmpty(state.sessionNotesLayoutSettings)
      && (
        (state.sessionNotesLayoutSettings.showPageHeader && !!(state.sessionNotesLayoutSettings.pageHeaderContent))
        || (state.sessionNotesLayoutSettings.showPageFooter && !!(state.sessionNotesLayoutSettings.pageFooterContent))
      );
  }, // hasSessionNotesLayout()

  lockedPrivileges: () => PRIVILEGES_LOCKED_BY_ROLE,

  defaultPrivileges: () => PRIVILEGES_BY_ROLE,

  userRoleList(state) {
    const userRoleLevel = this.userPrivilegesList
      .find((priv) => priv.value === (state.userDetails.userPrivilege || state.userDetails.role))
      ?.level ?? 1000;

    return this.userPrivilegesList.reduce((acc, userPrivileges) => {
      acc.push({
        ...userPrivileges,
        disable: userPrivileges.level < userRoleLevel,
      });
      return acc;
    }, []);
  },

  isAuthSessionExpired() {
    return this.authSessionStatus === AUTH_SESSIONS_STATUS.EXPIRED;
  },

  isAuthSessionExpiring() {
    return this.authSessionStatus === AUTH_SESSIONS_STATUS.EXPIRING;
  },
};
